import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// @mui
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
// components
import Scrollbar from '../components/scrollbar';
import { useDispatch } from 'react-redux';
import hit from 'src/services/manager';
import { setLoader } from 'src/redux/loader';
import { setToast } from 'src/redux/toast';

const PARTNER_USERS_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'createdAt', label: 'Created', alignRight: false },
  { id: 'lastOpenedApp', label: 'Last Opened App', alignRight: false },
];

export default function PartnerUsersPage() {
  const { partnerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [openAssign, setOpenAssign] = useState(false);
  const [searchForm, setSearchForm] = useState({ name: '', email: '', phone: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const partnerName = location.state?.partnerName;

  const fetchPartnerUsers = async () => {
    if (!partnerId) {
      setPartnerUsers([]);
      return;
    }
    try {
      dispatch(setLoader(true));
      const res = await hit(`/admin/partners/${partnerId}/users`, 'get', {
        sortBy: 'createdAt:desc',
        limit: 100,
        page: 1,
      });
      if (!res.err) {
        setPartnerUsers(Array.isArray(res.data) ? res.data : []);
      } else {
        dispatch(setToast({ show: true, message: res.msg, severity: 'error' }));
        setPartnerUsers([]);
      }
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to load partner users.', severity: 'error' }));
      setPartnerUsers([]);
    } finally {
      dispatch(setLoader(false));
    }
  };

  useEffect(() => {
    fetchPartnerUsers();
  }, [partnerId, dispatch]);

  const openAssignDialog = () => {
    setSearchForm({ name: '', email: '', phone: '' });
    setSearchResults([]);
    setSelectedUser(null);
    setOpenAssign(true);
  };

  const closeAssignDialog = () => {
    setOpenAssign(false);
  };

  const updateSearchForm = (field) => (event) => {
    setSearchForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const searchUsers = async () => {
    if (!searchForm.name.trim() && !searchForm.email.trim() && !searchForm.phone.trim()) {
      dispatch(setToast({ show: true, message: 'Enter a name, email, or phone to search.', severity: 'error' }));
      return;
    }
    try {
      dispatch(setLoader(true));
      const params = {};
      if (searchForm.name.trim()) params.name = searchForm.name.trim();
      if (searchForm.email.trim()) params.email = searchForm.email.trim();
      if (searchForm.phone.trim()) params.phone = searchForm.phone.trim();
      const res = await hit('/admin/users', 'get', params);
      if (!res.err) {
        const list = res.data?.results || res.data?.data || res.data || [];
        const qName = searchForm.name.trim().toLowerCase();
        const qEmail = searchForm.email.trim().toLowerCase();
        const qPhone = searchForm.phone.trim().toLowerCase();
        const filtered = Array.isArray(list)
          ? list.filter((user) => {
              const name = (user?.name || '').toLowerCase();
              const email = (user?.email || '').toLowerCase();
              const phone = String(user?.phone || '').toLowerCase();
              const matchName = qName ? name.includes(qName) : true;
              const matchEmail = qEmail ? email.includes(qEmail) : true;
              const matchPhone = qPhone ? phone.includes(qPhone) : true;
              return matchName && matchEmail && matchPhone;
            })
          : [];
        setSearchResults(filtered);
        setSelectedUser(null);
      } else {
        dispatch(setToast({ show: true, message: res.msg, severity: 'error' }));
        setSearchResults([]);
      }
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to search users.', severity: 'error' }));
      setSearchResults([]);
    } finally {
      dispatch(setLoader(false));
    }
  };

  const assignUserToPartner = async () => {
    if (!selectedUser?._id && !selectedUser?.id) {
      dispatch(setToast({ show: true, message: 'Select a user to assign.', severity: 'error' }));
      return;
    }
    try {
      dispatch(setLoader(true));
      const userId = selectedUser?._id || selectedUser?.id;
      const res = await hit(`/admin/users/${userId}`, 'patch', {
        partner: partnerId,
        partner_id: partnerId,
      });
      if (!res.err) {
        dispatch(setToast({ show: true, message: 'User assigned successfully.', severity: 'success' }));
        closeAssignDialog();
        fetchPartnerUsers();
      } else {
        dispatch(setToast({ show: true, message: res.msg, severity: 'error' }));
      }
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to assign user.', severity: 'error' }));
    } finally {
      dispatch(setLoader(false));
    }
  };

  return (
    <>
      <Helmet>
        <title> Partner Users | SuperSenses UI </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack>
            <Typography variant="h4" gutterBottom>
              Partner Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {partnerName || partnerId}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate('/dashboard/partners')}>
              Back to Partners
            </Button>
            <Button variant="contained" onClick={openAssignDialog}>
              Assign User
            </Button>
          </Stack>
        </Stack>

        <Card>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
            <Typography variant="h6">Users</Typography>
            <Typography variant="body2">Total: {partnerUsers.length}</Typography>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">SNo</TableCell>
                    {PARTNER_USERS_HEAD.map((headCell) => (
                      <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                        {headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partnerUsers.map((user, index) => (
                    <TableRow hover key={user?._id || user?.id || index}>
                      <TableCell padding="checkbox">{index + 1}</TableCell>
                      <TableCell align="left">{user?.name || '—'}</TableCell>
                      <TableCell align="left">{user?.email || '—'}</TableCell>
                      <TableCell align="left">{user?.phone || '—'}</TableCell>
                      <TableCell align="left">
                        {user?.createdAt ? moment(user.createdAt).format('MM/DD/YYYY hh:mm a') : '—'}
                      </TableCell>
                      <TableCell align="left">
                        {user?.lastOpenedApp
                          ? moment(user.lastOpenedApp).format('MM/DD/YYYY hh:mm a')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {partnerUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" sx={{ py: 2 }}>
                          No users assigned to this partner.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>

      <Dialog open={openAssign} onClose={closeAssignDialog} maxWidth="md" fullWidth>
        <DialogTitle>Assign User to Partner</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="subtitle1">Search user</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Name" value={searchForm.name} onChange={updateSearchForm('name')} fullWidth />
              <TextField label="Email" value={searchForm.email} onChange={updateSearchForm('email')} fullWidth />
              <TextField label="Phone" value={searchForm.phone} onChange={updateSearchForm('phone')} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={searchUsers}>
                Search
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Enter any field to search.
              </Typography>
            </Stack>
            <Divider />
            <Typography variant="subtitle1">Results</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Select</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((user) => {
                    const userId = user?._id || user?.id;
                    return (
                      <TableRow
                        hover
                        key={userId}
                        selected={(selectedUser?._id || selectedUser?.id) === userId}
                        onClick={() => setSelectedUser(user)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          {(selectedUser?._id || selectedUser?.id) === userId ? '✓' : ''}
                        </TableCell>
                        <TableCell>{user?.name || '—'}</TableCell>
                        <TableCell>{user?.email || '—'}</TableCell>
                        <TableCell>{user?.phone || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                  {searchResults.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" sx={{ py: 2 }}>
                          No users found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog}>Cancel</Button>
          <Button variant="contained" onClick={assignUserToPartner} disabled={!selectedUser}>
            Assign User
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
